export const CO2_YEARS = ["2030", "2050", "2100"];

// В исходной таблице потенциал поглощения задан один раз на группу
// месторождений (объединённая ячейка), а при загрузке в БД это значение
// попадает в каждое месторождение группы. Для сумм по субъектам/округам/
// России такую группу нужно считать один раз, иначе итог завышается в
// разы. Группа = субъект + набор значений по годам.
export const dedupeCo2Points = (points) => {
    const seen = new Set();
    return points.filter((point) => {
        const values = CO2_YEARS.map((y) => point.co2Absorption?.[y] ?? 0);
        if (!values.some((v) => v > 0)) return false;
        const key = `${point.region}|${values.join("|")}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const sumCo2 = (points, year) =>
    dedupeCo2Points(points).reduce(
        (sum, point) => sum + (point.co2Absorption?.[year] ?? 0),
        0
    );

export const sumCo2ForYear = sumCo2;

// Бэкенд (/api/region_agg) отдаёт суммы только за первый год, поэтому
// суммы по округам и субъектам для выбранного года считаем из точек.
export const getCo2Totals = (points, year) => {
    const districts = {};
    const regions = {};

    dedupeCo2Points(points).forEach((point) => {
        const value = point.co2Absorption?.[year] ?? 0;
        if (point.federalDistrict) {
            districts[point.federalDistrict] =
                (districts[point.federalDistrict] || 0) + value;
        }
        if (point.region) {
            regions[point.region] = (regions[point.region] || 0) + value;
        }
    });

    return { districts, regions };
};

// Округляет вверх до двух значащих цифр: 8 660 942 -> 8 700 000.
const roundUpNice = (value) => {
    if (value <= 0) return 0;
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)) - 1);
    return Math.ceil(value / magnitude) * magnitude;
};

// Верхняя граница шкалы градиента. Считается сразу по всем годам, чтобы
// шкала была фиксированной: цвет меняется при переключении года, а не
// "переезжает" вслед за максимумом текущего года. Один-два региона на
// порядок больше остальных, поэтому берём не абсолютный максимум, а 90-й
// перцентиль: такие регионы просто "зашкаливают" в тёмно-красный, а у
// остальных появляется читаемая разница в оттенках.
export const getGradientMax = (points) => {
    const byRegion = {};
    points.forEach((point) => {
        if (!point.region) return;
        (byRegion[point.region] = byRegion[point.region] || []).push(point);
    });

    const regionMaxes = Object.values(byRegion)
        .map((regionPoints) =>
            Math.max(...CO2_YEARS.map((year) => sumCo2(regionPoints, year)))
        )
        .filter((value) => value > 0)
        .sort((a, b) => a - b);

    if (!regionMaxes.length) return 0;
    return roundUpNice(regionMaxes[Math.floor(0.9 * (regionMaxes.length - 1))]);
};

export const formatCo2 = (value) =>
    `${Number(value).toFixed(2).replace(".", ",")} тыс. т`;
